import React, { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useRouter } from "next/router"
import Head from "next/head"

import { Container, Row, Col, Nav, Tab, Pagination } from "react-bootstrap"

import { listPropertyData } from "@/slices/listPropertiesSlice"

import Loader from "@/main/loader"

import PropertySearch from "@/forms/propertySearch"

import OrderByField from "@/fields/orderByField"
import AvailableOnlyField from "@/fields/availableOnlyField"
import PropertyCardList from "@/propertyList/propertyCardList"
import PropertyCardGrid from "@/propertyList/propertyCardGrid"

function PropertyListScreen() {
    const dispatch = useDispatch()

    const url = "https://webbjenkins.co.uk/properties/"
    const title = "Our property listings - Webb Jenkins"
    const description =
        "Browse through our property portfolio and if you find a property you like then don't hesitate to contact us to see if it is still available."
    const imgUrl =
        "https://webb-jenkins-static-164679926.imgix.net/socials/webbjenkins-listings-social.jpg"
    const imgWidth = 1200
    const imgHeight = 630
    const imgAlt = "Webb Jenkins logo with our property listings caption"

    const { status, properties, next, prev, count } = useSelector(
        (state) => state.listProperties,
    )

    const router = useRouter()

    const { page, layout, ordering, trans_type_id } = router.query
    const totalPages = Math.ceil(count / 12)

    const prevNumbers = Array.from({ length: page - 1 }, (_, i) => i + 1)
    const nextNumbers = Array.from(
        {
            length: totalPages - page,
        },
        (_, i) => parseInt(page) + i + 1,
    )

    const changePage = (newPage) => {
        router.query.page = newPage
        router.push(router)
    }

    useEffect(() => {
        if (!router.isReady) {
            return (
                <>
                    <Loader />
                </>
            )
        } else if (!page || !layout || !ordering || !trans_type_id) {
            !page && (router.query.page = 1)
            !layout && (router.query.layout = "list")
            !ordering && (router.query.ordering = "-update_date")
            !trans_type_id && (router.query.trans_type_id = 1)
            router.push(router)
        } else {
            const propertyListParams = router.asPath.slice(11)
            dispatch(listPropertyData(propertyListParams))
        }
    }, [
        router,
        router.query,
        router.isReady,
        dispatch,
        page,
        layout,
        ordering,
        trans_type_id,
    ])

    return (
        <>
            <Head>
                <link rel="canonical" href={url} />
                <title>{title}</title>
                <meta name="description" content={description} />

                <meta property="fb:app_id" content="362437448786240" />
                <meta property="og:title" content={title} />
                <meta property="og:image:url" content={imgUrl} />
                <meta property="og:image:width" content={imgWidth} />
                <meta property="og:image:height" content={imgHeight} />
                <meta property="og:image:alt" content={imgAlt} />
                <meta property="og:description" content={description} />
                <meta property="og:url" content={url} />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="en" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:image:src" content={imgUrl} />
                <meta name="twitter:site" content="@WebbJenkinsIOW" />
            </Head>
            <PropertySearch />

            {status === "loading" ? (
                <>
                    <Loader />
                </>
            ) : status === "error" ? (
                <>
                    <Container fluid="md" className="mt-5 mb-12">
                        <h1 className="h3">
                            Couldn&apos;t retrieve properties
                        </h1>
                        <p>
                            Unfortunately we couldn&apos;t retrieve our property
                            listings at this moment in time. Please try
                            refreshing and call us on <b>01983 856111</b> or
                            email us at{" "}
                            <b>propertyservices@webbjenkins.co.uk</b> if the
                            problem persists.
                        </p>
                    </Container>
                </>
            ) : (
                <>
                    {/* If there are properties for the search criteria then render them*/}
                    {properties?.length !== 0 ? (
                        <>
                            <Container fluid="md" className="mt-5 ">
                                <Row className="d-flex">
                                    <Tab.Container
                                        defaultActiveKey={
                                            layout ? layout : "list"
                                        }
                                        className="ms-auto"
                                    >
                                        <Col className="mb-5">
                                            <h1 className="h4 mb-4">
                                                Currently showing{" "}
                                                {(page - 1) * 12 + 1}-
                                                {(page - 1) * 12 +
                                                    properties.length}{" "}
                                                of {count} properties
                                            </h1>
                                            <Row>
                                                <OrderByField className="me-aut" />
                                                <AvailableOnlyField />
                                            </Row>
                                        </Col>
                                        <Col className="justify-content-end d-none d-lg-flex mb-5">
                                            <Col
                                                md={6}
                                                className="d-flex align-items-end ps-md-4 pe-md-2 pt-md-3"
                                            >
                                                <Nav
                                                    as={Col}
                                                    xs={12}
                                                    justify
                                                    variant="pills"
                                                    className="ms-md-1 mt-md-2 nav-pills border"
                                                >
                                                    <Nav.Item>
                                                        <Nav.Link
                                                            role="button"
                                                            href="#"
                                                            eventKey="list"
                                                            style={{
                                                                cursor: "pointer",
                                                                height: "3rem",
                                                            }}
                                                            onClick={(e) => {
                                                                e.preventDefault()
                                                                router.query.layout =
                                                                    "list"

                                                                router.push(
                                                                    router,
                                                                )
                                                            }}
                                                            className="text-sm text-bold d-flex align-items-center justify-content-center"
                                                        >
                                                            List
                                                        </Nav.Link>
                                                    </Nav.Item>
                                                    <Nav.Item>
                                                        <Nav.Link
                                                            role="button"
                                                            href="#"
                                                            eventKey="grid"
                                                            style={{
                                                                cursor: "pointer",
                                                                height: "3rem",
                                                            }}
                                                            onClick={(e) => {
                                                                e.preventDefault()
                                                                router.query.layout =
                                                                    "grid"

                                                                router.push(
                                                                    router,
                                                                )
                                                            }}
                                                            className="text-sm text-bold d-flex align-items-center justify-content-center"
                                                        >
                                                            Grid
                                                        </Nav.Link>
                                                    </Nav.Item>
                                                </Nav>
                                            </Col>
                                        </Col>

                                        <Tab.Content className="m-0 p-0">
                                            <Tab.Pane eventKey="list">
                                                {properties.map(
                                                    (property, index) => (
                                                        <PropertyCardList
                                                            key={property.id}
                                                            property={property}
                                                            priority={
                                                                index === 0
                                                            }
                                                        />
                                                    ),
                                                )}
                                            </Tab.Pane>
                                            <Tab.Pane eventKey="grid">
                                                <Row className="d-flex">
                                                    {properties.map(
                                                        (property, index) => (
                                                            <Col
                                                                key={
                                                                    property.id
                                                                }
                                                                sm={12}
                                                                lg={6}
                                                                xxl={4}
                                                                className=" d-flex align-items-stretch"
                                                            >
                                                                <PropertyCardGrid
                                                                    key={
                                                                        property.id
                                                                    }
                                                                    property={
                                                                        property
                                                                    }
                                                                    priority={
                                                                        index ===
                                                                        0
                                                                    }
                                                                />
                                                            </Col>
                                                        ),
                                                    )}
                                                </Row>
                                            </Tab.Pane>
                                        </Tab.Content>
                                        <Row className="justify-content-center">
                                            <Col>
                                                <Pagination size="lg">
                                                    {page > 4 && (
                                                        <Pagination.First
                                                            key="firstPage"
                                                            onClick={() =>
                                                                changePage(1)
                                                            }
                                                        />
                                                    )}

                                                    {prev && (
                                                        <Pagination.Prev
                                                            key="prevPage"
                                                            rel="prev"
                                                            onClick={() => {
                                                                const newPage =
                                                                    parseInt(
                                                                        page,
                                                                    ) - 1
                                                                changePage(
                                                                    newPage,
                                                                )
                                                            }}
                                                        />
                                                    )}

                                                    {prevNumbers.length > 2 ? (
                                                        prevNumbers
                                                            .slice(
                                                                prevNumbers.length -
                                                                    3,
                                                                prevNumbers.length,
                                                            )
                                                            .map((number) => (
                                                                <Pagination.Item
                                                                    key={number}
                                                                    onClick={() => {
                                                                        changePage(
                                                                            number,
                                                                        )
                                                                    }}
                                                                >
                                                                    {number}
                                                                </Pagination.Item>
                                                            ))
                                                    ) : prevNumbers.length ===
                                                      2 ? (
                                                        <>
                                                            <Pagination.Item
                                                                key={
                                                                    prevNumbers[0]
                                                                }
                                                                onClick={() => {
                                                                    changePage(
                                                                        prevNumbers[0],
                                                                    )
                                                                }}
                                                            >
                                                                {prevNumbers[0]}
                                                            </Pagination.Item>
                                                            <Pagination.Item
                                                                key={
                                                                    prevNumbers[1]
                                                                }
                                                                onClick={() => {
                                                                    changePage(
                                                                        prevNumbers[1],
                                                                    )
                                                                }}
                                                            >
                                                                {prevNumbers[1]}
                                                            </Pagination.Item>
                                                        </>
                                                    ) : prevNumbers.length ===
                                                      1 ? (
                                                        <Pagination.Item
                                                            key={prevNumbers[0]}
                                                            onClick={() => {
                                                                changePage(
                                                                    prevNumbers[0],
                                                                )
                                                            }}
                                                        >
                                                            {prevNumbers[0]}
                                                        </Pagination.Item>
                                                    ) : (
                                                        <></>
                                                    )}

                                                    <Pagination.Item
                                                        key={page}
                                                        active
                                                    >
                                                        {page}
                                                    </Pagination.Item>

                                                    {nextNumbers.length > 2 ? (
                                                        nextNumbers
                                                            .slice(0, 3)
                                                            .map((number) => (
                                                                <Pagination.Item
                                                                    key={number}
                                                                    onClick={() => {
                                                                        changePage(
                                                                            number,
                                                                        )
                                                                    }}
                                                                >
                                                                    {number}
                                                                </Pagination.Item>
                                                            ))
                                                    ) : nextNumbers.length ===
                                                      2 ? (
                                                        <>
                                                            <Pagination.Item
                                                                key={
                                                                    nextNumbers[0]
                                                                }
                                                                onClick={() => {
                                                                    changePage(
                                                                        nextNumbers[0],
                                                                    )
                                                                }}
                                                            >
                                                                {nextNumbers[0]}
                                                            </Pagination.Item>
                                                            <Pagination.Item
                                                                key={
                                                                    nextNumbers[1]
                                                                }
                                                                onClick={() => {
                                                                    changePage(
                                                                        nextNumbers[1],
                                                                    )
                                                                }}
                                                            >
                                                                {nextNumbers[1]}
                                                            </Pagination.Item>
                                                        </>
                                                    ) : nextNumbers.length ===
                                                      1 ? (
                                                        <Pagination.Item
                                                            key={nextNumbers[0]}
                                                            onClick={() => {
                                                                changePage(
                                                                    nextNumbers[0],
                                                                )
                                                            }}
                                                        >
                                                            {nextNumbers[0]}
                                                        </Pagination.Item>
                                                    ) : (
                                                        <></>
                                                    )}

                                                    {next && (
                                                        <Pagination.Next
                                                            key="nextPage"
                                                            rel="next"
                                                            onClick={() => {
                                                                const newPage =
                                                                    parseInt(
                                                                        page,
                                                                    ) + 1
                                                                changePage(
                                                                    newPage,
                                                                )
                                                            }}
                                                        />
                                                    )}
                                                    {page < totalPages - 3 && (
                                                        <Pagination.Last
                                                            key="lastPage"
                                                            onClick={() =>
                                                                changePage(
                                                                    totalPages,
                                                                )
                                                            }
                                                        />
                                                    )}
                                                </Pagination>
                                            </Col>
                                        </Row>
                                    </Tab.Container>
                                </Row>
                            </Container>
                        </>
                    ) : (
                        <Container fluid="md" className="mt-5 mb-12">
                            <h1 className="h3">No properties found</h1>
                            <p>
                                Your search criteria returned no results. Please
                                try again with different criteria, or contact us
                                directly to find a property.
                            </p>
                        </Container>
                    )}
                </>
            )}
        </>
    )
}

export default PropertyListScreen
